import { TableDataProps } from "@/pages/Marketplace";
import { TableDataProps as SalesListTableDataProps } from "@/pages/SalesList";
import { TypePageActions } from "@/pages/Plans";
import { ApiDataProps } from "@/pages/Tenants";
import { Mask } from "@/utils/mask";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Search,
  Eye,
  Trash,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Button } from "@/components/ui/button";
import { ReceiptDialog } from "@/components/ui/receipt-dialog";
import { useState } from "react";
import { CancelSaleModal } from "@/components/CancelSaleModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/auth";
import { SALE_STATUS } from "@/utils/constants";
import { getSaleStatusBadge } from "@/constants/saleStatus";

type TableProps = {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: TypePageActions;
  updateFront: (data: any) => void;
  sortConfig: { column: string; direction: string };
  onSort: (column: string) => void;
  onRefresh: () => void | Promise<void>;
};

export const TableData = ({
  dataApi,
  pageActions,
  sortConfig,
  onSort,
  onRefresh,
}: TableProps) => {
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedSale, setSelectedSale] =
    useState<SalesListTableDataProps | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [saleToCancel, setSaleToCancel] = useState<TableDataProps | null>(null);
  const { hasPermission } = usePermissions();
  const user = useAuth();
  const sellerId = user?.user?.seller?.id;
  const canCancelSale = hasPermission("delete-sales") && !sellerId;

  const handleViewReceipt = (sale: TableDataProps) => {
    const convertedSale = sale as any as SalesListTableDataProps;
    setSelectedSale(convertedSale);
    setShowReceipt(true);
  };

  const handleCancelSale = (sale: TableDataProps) => {
    setSaleToCancel(sale);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setSaleToCancel(null);
  };

  const handleSaleCancelled = async () => {
    await onRefresh();
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm", {
        locale: ptBR,
      });
    } catch {
      return "-";
    }
  };

  const getSortIcon = (column: string) => {
    if (sortConfig.column !== column) {
      return <Search className="h-4 w-4 ml-1" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="h-4 w-4 ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 ml-1" />
    );
  };

  const SortableHeader = ({
    column,
    children,
    className = "",
  }: {
    column: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead className={className}>
      <Button
        variant="ghost"
        onClick={() => onSort(column)}
        className="h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] hover:bg-transparent"
      >
        {children}
        {getSortIcon(column)}
      </Button>
    </TableHead>
  );

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <SortableHeader column="id">ID</SortableHeader>
            <TableHead>Venda pelo Marketplace</TableHead>
            <TableHead>Produtos</TableHead>
            <TableHead>Concurso</TableHead>
            <TableHead>Loteria</TableHead>
            <SortableHeader column="created_by">Criado por</SortableHeader>
            <TableHead>Tipo de Venda</TableHead>
            <TableHead>Status da Venda</TableHead>
            <SortableHeader column="total">Valor Total</SortableHeader>
            <SortableHeader column="created_at">Data de Criação</SortableHeader>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.id}</TableCell>
              <TableCell>{item?.client?.name || "Marketplace"}</TableCell>
              <TableCell>
                {item.products && item.products.length > 0 ? (
                  <>
                    {item.products.slice(0, 3).map((product, idx) => (
                      <span key={product.id || idx}>
                        {product.product_name}
                        {idx < Math.min(item.products.length, 3) - 1
                          ? ", "
                          : ""}
                      </span>
                    ))}
                    {item.products.length > 3 && (
                      <span>
                        {" "}
                        + {item.products.length - 3} produto
                        {item.products.length - 3 > 1 ? "s" : ""}
                      </span>
                    )}
                  </>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>{item.products?.[0]?.contest?.name || "-"}</TableCell>
              <TableCell>
                {item.products?.[0]?.contest?.lottery ? (
                  <span
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold text-white"
                    style={{ backgroundColor: item.products[0].contest.lottery.color || '#6b7280' }}
                  >
                    {item.products[0].contest.lottery.name}
                  </span>
                ) : "-"}
              </TableCell>
              <TableCell>{item.created_by?.name || "-"}</TableCell>
              <TableCell>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${getSaleStatusBadge(item.sale_status_id)}`}
                >
                  {item.sale_status?.name || "-"}
                </span>
              </TableCell>
              <TableCell>{item.sale_type?.name || "-"}</TableCell>
              <TableCell>
                {Mask.formatCurrency(parseFloat(item?.total || "0"))}
              </TableCell>
              <TableCell>{formatDate(item.created_at)}</TableCell>
              <TableCell className="text-center">
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={() => handleViewReceipt(item)}
                    title="Ver Comprovante"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {canCancelSale &&
                    item.sale_status_id !== SALE_STATUS.CANCELED && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-stone-950/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelSale(item);
                        }}
                        title="Cancelar venda"
                      >
                        <Trash className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={10}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <ReceiptDialog
        sale={selectedSale}
        open={showReceipt}
        onOpenChange={setShowReceipt}
      />
      <CancelSaleModal
        isOpen={cancelModalOpen}
        onClose={handleCloseCancelModal}
        sale={saleToCancel}
        onSaleCancelled={handleSaleCancelled}
      />
    </>
  );
};
